import { useTranslation } from 'react-i18next';
import { MainCustomButton, SecondaryCustomButton } from '../../components/Buttons';
import { CustomInput } from '../../components/inputs';
import { AuthPageLayout } from './AuthPageLayout';
import * as Yup from 'yup';
import { useForm, type SubmitHandler } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { AuthService } from '../../services/AuthService';
import { useEffect, useMemo, useState } from 'react';
import { errorToast } from '../../utils/toasts';
import { errorMessageAdapter } from '../../adapters';
import { AxiosError, AxiosInstance } from 'axios';
import i18n from '../../plugins/i18n/i18n';
import { useApi } from '../../services/hooks/apiHook';
import { useAppSelector } from '../../hooks/hooksRedux';
import { RootState } from '../../store';
import { randomInt } from '../../utils/random';
import { useNavigate } from 'react-router-dom';

interface authInfos {
  email: string;
}

const ForgotPasswordPage: React.FunctionComponent = () => {
  const { t } = useTranslation();
  const [isDisabled, setIsDisabled] = useState(false);
  const [loading, setLoading] = useState(false);
  const [countdown, setCountdown] = useState<number | null>(null);
  const { logo } = useAppSelector((state: RootState) => state.styleGuide.data);
  const navigate = useNavigate();

  const randomImage = useMemo(() => randomInt(1, 5), []);

  const api: AxiosInstance = useApi();

  const validationSchema = Yup.object().shape({
    email: Yup.string()
      .required(t('validations.valRequired') || '')
      .email(t('validations.valInvalidEmail') || '')
  });

  const {
    register,
    handleSubmit,
    formState: { errors }
  } = useForm<authInfos>({
    resolver: yupResolver(validationSchema)
  });

  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (countdown !== null && countdown > 0) {
      timer = setInterval(() => setCountdown(countdown - 1), 1000);
      setIsDisabled(true);
    } else if (countdown === 0) {
      setIsDisabled(false);
      setCountdown(null);
    }

    return () => {
      clearInterval(timer);
    };
  }, [countdown]);

  const handleSubmitForm: SubmitHandler<authInfos> = async (data: authInfos) => {
    setLoading(true);
    try {
      await AuthService.forgotPassword(api, data, {
        headers: { 'Content-Language': i18n.language.toLowerCase() }
      });
      setLoading(false);
      setIsDisabled(true);
      setCountdown(60);
    } catch (e: any) {
      setLoading(false);
      setIsDisabled(false);
      errorToast(errorMessageAdapter(e as AxiosError));
    }
  };

  return (
    <AuthPageLayout
      imageIndex={randomImage}
      imageText={t('labels.lblWeveHelped')}
      logo={logo}
      title={t('titles.ttlResetPwd')}
      cardClassName="max-w-[520px]"
    >
      <form id="MainForm" className="flex w-full flex-col items-start justify-center gap-7" onSubmit={handleSubmit(handleSubmitForm)}>
        <p className="-mt-2 text-sm leading-6 text-slate-500">{t('others.othResetPwdInstruction')}</p>

        <div className="flex w-full flex-col gap-5">
          <CustomInput
            id="email"
            label={t('labels.lblEmail')}
            placeholder={t('fields.fldEmailPlaceholder') || ' '}
            type="email"
            error={errors.email?.message}
            register={register}
          />
        </div>
        <div className="flex w-full flex-col items-center gap-4 pt-1 sm:flex-row">
          <SecondaryCustomButton type="button" onClick={() => navigate('/login')} text={t('buttons.btnBack') || 'Back'} className="!max-w-none" />
          <MainCustomButton
            text={countdown ? `${t('buttons.btnResetPwd')} (${countdown})` : t('buttons.btnResetPwd')}
            form="MainForm"
            disabled={isDisabled}
            loading={loading}
            className="!max-w-none"
          />
        </div>
      </form>
    </AuthPageLayout>
  );
};

export default ForgotPasswordPage;
